import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'octoink_cart_v1';

export function CartProvider({ children }) {
    const [cartItems, setCartItems] = useState(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch {
            return [];
        }
    });

    const [isCartOpen, setIsCartOpen] = useState(false);
    const [toast, setToast] = useState(null); // { message, type }

    // Persist to localStorage whenever cart changes
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(cartItems));
        } catch (e) {
            console.warn('Cart persistence failed:', e);
        }
    }, [cartItems]);

    // Auto-dismiss toast
    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 2800);
        return () => clearTimeout(t);
    }, [toast]);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const addToCart = useCallback((product) => {
        setCartItems(prev => {
            const exists = prev.find(item => item._id === product._id);
            if (exists) {
                showToast(`"${product.title}" is already in your cart`, 'info');
                return prev.map(item =>
                    item._id === product._id
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            }
            showToast(`"${product.title}" added to cart`, 'success');
            return [
                ...prev,
                {
                    _id: product._id,
                    title: product.title,
                    category: product.category,
                    price: product.price,
                    originalPrice: product.originalPrice,
                    image: product.image,
                    hasFile: product.hasFile,
                    quantity: 1,
                }
            ];
        });
    }, []);

    const removeFromCart = useCallback((productId) => {
        setCartItems(prev => prev.filter(item => item._id !== productId));
    }, []);

    const updateQuantity = useCallback((productId, delta) => {
        setCartItems(prev =>
            prev
                .map(item =>
                    item._id === productId
                        ? { ...item, quantity: Math.max(1, item.quantity + delta) }
                        : item
                )
        );
    }, []);

    const clearCart = useCallback(() => {
        setCartItems([]);
    }, []);

    const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalPrice = cartItems.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);

    return (
        <CartContext.Provider value={{
            cartItems,
            isCartOpen,
            setIsCartOpen,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            totalItems,
            totalPrice,
            toast,
        }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error('useCart must be used within CartProvider');
    return ctx;
}
